import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import EmptyState from "@/components/feature/EmptyState";
import Toast from "@/components/base/Toast";
import Toc from "@/pages/detail/article/components/Toc";
import ShareBar from "@/pages/detail/article/components/ShareBar";
import { getArticle, formatDate, formatViews, relatedArticles, categoryMap } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

export default function ArticleDetailPage() {
  const { articleId } = useParams();
  const article = getArticle(articleId);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  useSeo({
    title: article ? `${article.title} - 抖阳原创内容` : "内容不存在 - 抖阳",
    description: article ? article.summary : "该内容不存在或已被删除，返回抖阳首页浏览最新原创报道。",
    keywords: article ? `抖阳,${categoryMap[article.category]?.name},${article.tags.join(",")},原创内容` : "抖阳,原创内容",
  });

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0 });
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [articleId]);

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2400);
  };

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[900px] flex-1 px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-file-unknow-line"
            title="内容不存在或已被删除"
            description="这篇文章可能已被作者撤回，或者链接有误。你可以回到首页继续浏览最新原创内容。"
            action={
              <Link
                to="/"
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600"
              >
                返回首页
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = categoryMap[article.category];
  const related = relatedArticles(article, 5);
  const relatedCards = related.slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100/60 px-4 py-3 md:px-6">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-2">
            <Breadcrumb
              items={[{ label: "首页", to: "/" }, { label: category?.name ?? "分类", to: `/category/${article.category}` }, { label: "文章详情" }]}
            />
            <Link
              to={`/category/${article.category}`}
              className="flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回{category?.name}频道
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="mx-auto max-w-[900px] px-4 py-10 md:px-6">
            <div className="skeleton-shimmer h-4 w-24 rounded" />
            <div className="skeleton-shimmer mt-4 h-9 w-full rounded" />
            <div className="skeleton-shimmer mt-3 h-9 w-3/4 rounded" />
            <div className="skeleton-shimmer mt-6 h-4 w-1/2 rounded" />
            <div className="skeleton-shimmer mt-8 aspect-[16/9] w-full rounded-lg" />
            <div className="mt-8 space-y-3">
              {Array.from({ length: 8 }).map((_, index) => (
                <div key={index} className="skeleton-shimmer h-4 w-full rounded" />
              ))}
            </div>
          </div>
        ) : (
          <article>
            <header className="mx-auto max-w-[900px] px-4 pb-6 pt-8 md:px-6 md:pt-10">
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/category/${article.category}`}
                  className="rounded bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  {category?.name}
                </Link>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/search?q=${encodeURIComponent(tag)}`}
                    className="rounded border border-background-300 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    {tag}
                  </Link>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug tracking-tight text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-500 md:text-base">{article.subtitle}</p>

              <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-background-200 py-4">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                    {article.author.name.slice(0, 1)}
                  </span>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{article.author.name}</p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-foreground-400">
                      <span>{article.author.role}</span>
                      <span className="text-background-400">·</span>
                      <span>{formatDate(article.date)}</span>
                      <span className="text-background-400">·</span>
                      <span>{article.readMinutes} 分钟阅读</span>
                      <span className="text-background-400">·</span>
                      <span>{formatViews(article.views)} 阅读</span>
                    </p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 text-[11px] text-foreground-500">
                  <i className="ri-award-line text-primary-500" />
                  抖阳原创内容
                </span>
              </div>
            </header>

            <div className="mx-auto max-w-[1100px] px-4 md:px-6">
              <figure className="overflow-hidden rounded-lg bg-background-200">
                <div className="aspect-[16/9] w-full">
                  <img
                    src={article.cover}
                    alt={`${article.title} - 封面图`}
                    title={`${article.title} - 抖阳原创内容`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <figcaption className="bg-background-100 px-4 py-2.5 text-xs text-foreground-500">
                  图 / 抖阳视觉中心 · {article.title}
                </figcaption>
              </figure>
            </div>

            <div className="mx-auto mt-8 grid max-w-[1100px] grid-cols-1 gap-8 px-4 pb-10 md:px-6 lg:grid-cols-[1fr_260px] lg:gap-12">
              <div className="min-w-0">
                <Toc items={article.sections.map((section) => ({ id: section.id, heading: section.heading }))} />

                <div className="article-body">
                  {article.sections.map((section) => (
                    <section key={section.id} id={section.id} className="scroll-mt-24 pb-2">
                      <h2 className="mb-4 mt-8 font-heading text-lg font-bold text-foreground-950 md:text-xl">
                        {section.heading}
                      </h2>
                      {section.paragraphs.map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                      ))}
                      {section.image ? (
                        <figure className="my-6 overflow-hidden rounded-lg border border-background-200">
                          <div className="aspect-[16/10] w-full">
                            <img
                              src={section.image}
                              alt={section.imageCaption ?? article.title}
                              title={`${article.title} - 现场图片`}
                              className="h-full w-full object-cover object-top"
                            />
                          </div>
                          {section.imageCaption ? (
                            <figcaption className="bg-background-100 px-3 py-2 text-xs text-foreground-500">
                              {section.imageCaption}
                            </figcaption>
                          ) : null}
                        </figure>
                      ) : null}
                    </section>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200 pt-5">
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/search?q=${encodeURIComponent(tag)}`}
                      className="flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-800"
                    >
                      <i className="ri-price-tag-3-line" />
                      {tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-5">
                  <ShareBar title={article.title} onNotify={notify} />
                </div>

                <div className="mt-6 rounded-lg border border-background-200 bg-background-100/70 p-4 md:p-5">
                  <p className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-information-line text-primary-500" />
                    本文为抖阳原创演示内容，数据与人物均为示例，不构成任何事实陈述。
                  </p>
                  <div className="mt-4 flex items-start gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background-200 font-heading text-sm font-bold text-foreground-700">
                      {article.author.name.slice(0, 1)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{article.author.name}</p>
                      <p className="mt-1 text-xs leading-relaxed text-foreground-500">{article.author.bio}</p>
                    </div>
                  </div>
                </div>
              </div>

              <aside className="min-w-0">
                <div className="rounded-lg border border-background-200 bg-background-50 p-4 lg:sticky lg:top-24">
                  <p className="mb-1 font-label text-[10px] uppercase tracking-[0.22em] text-primary-600">Related</p>
                  <h2 className="mb-3 border-b border-background-200 pb-2.5 font-heading text-sm font-bold text-foreground-950">
                    {category?.name}频道最新
                  </h2>
                  <ul className="space-y-2.5">
                    {related.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/detail/article/${item.id}`}
                          className="group block"
                        >
                          <span className="line-clamp-2-custom block text-xs leading-relaxed text-foreground-700 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-[11px] text-foreground-400">
                            {formatDate(item.date)} · {formatViews(item.views)} 阅读
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/category/${article.category}`}
                    className="mt-4 flex items-center justify-center gap-1.5 rounded-md border border-background-300 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                  >
                    查看全部{category?.name}内容
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </aside>
            </div>
          </article>
        )}

        <section className="border-t border-background-200 bg-background-100/60 px-4 py-10 md:px-6 md:py-12">
          <div className="mx-auto max-w-[1400px]">
            <div className="mb-5 flex items-end justify-between gap-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">继续阅读</h2>
              <span className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-400">
                More stories
              </span>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {relatedCards.map((item) => (
                <ArticleCard key={item.id} article={item} showSummary={false} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
      <Toast message={toast} visible={Boolean(toast)} />
    </div>
  );
}